import { PAGE_TYPE_PRODUCT } from 'shared/consts'
import { PDP_APP_BANNER, PDP_APP_BANNER_AT } from 'shared/experiments/consts'
import {
  activateExperiment,
  getExperiments,
} from 'shared/experiments/utils/experiments'
import { bindTrackingEvents } from 'shared/experiments/utils/tracking'
import store from 'shared/store'
import type { Experiment } from 'types/shopConfig'

import initBanner from './banner'
import {
  BANNER_EVENT,
  BANNER_KEYS,
  EVENT_CLICK_CLOSE,
  EVENT_CLICK_CTA,
  EXPERIMENT_TENANT,
} from './consts'

import './styles.scss'

const getTenant = () => {
  const { tenant } = store.getPublicRuntimeConfig()?.publicConfig || {}

  return tenant
}

const isExperimentEnabled = (experiments: Experiment[]) =>
  experiments.some(experiment => {
    const { variant, isEnabled } = experiment
    const isVariantValid = variant === 'variation_1' || variant === 'v1'

    return isEnabled && isVariantValid
  })

const isEnvValid = (): boolean => {
  const pageProperties = store.getPublicRuntimeConfig()?.pageProperties || {}
  const { pageType, productId, experiments = [] } = pageProperties

  const experimentsList = getExperiments(
    [PDP_APP_BANNER, PDP_APP_BANNER_AT],
    experiments
  )
  return Boolean(
    pageType === PAGE_TYPE_PRODUCT &&
      window.dataLayer &&
      productId &&
      isExperimentEnabled(experimentsList)
  )
}

const handleBannerEvents = () => {
  if (!window.AF) {
    return
  }

  window.AF('banners', 'showBanner')
}

const clearGtmStyles = () => {
  const element = document.getElementById('body')
  element?.classList.remove('with-nc-banner')
}

const isBannerInserted = (addedNode: Node): boolean =>
  addedNode.nodeType === 1 &&
  (<Element>addedNode).matches('.responsive-wrapper')

const handleMutations = (
  mutations: MutationRecord[],
  callback: CallableFunction
) => {
  for (const mutation of mutations) {
    const banner = Array.from(mutation.addedNodes).find(isBannerInserted)

    if (banner) {
      callback()
      break
    }
  }
}

const observeBanner = (callback: CallableFunction) => {
  // eslint-disable-next-line prefer-const
  let observer: MutationObserver

  const stopObserving = () => {
    callback()
    observer.disconnect()
  }

  observer = new MutationObserver(mutations =>
    handleMutations(mutations, stopObserving)
  )

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  })
}

const bindEventsTracking = () => {
  bindTrackingEvents([
    {
      selector: '.responsive-wrapper button[data-role=cta]',
      event: 'click',
      trackingEvent: EVENT_CLICK_CTA,
    },
    {
      selector: '.responsive-wrapper [data-af-close-button=true]',
      event: 'click',
      trackingEvent: EVENT_CLICK_CLOSE,
    },
  ])
}

const appendBannerToHeader = () => initBanner(BANNER_KEYS[getTenant()])

const handleBannerInsertion = () => {
  clearGtmStyles()
  window.dataLayer.push({ event: BANNER_EVENT })
  bindEventsTracking()
}

const callExperimentActivation = () => {
  const experimentName = EXPERIMENT_TENANT[getTenant()]

  if (experimentName) {
    void activateExperiment(experimentName)
  }
}

const init = () => {
  if (!isEnvValid()) {
    return
  }

  observeBanner(handleBannerInsertion)
  appendBannerToHeader()
  handleBannerEvents()
  callExperimentActivation()
}

export default init
